import React, { Fragment } from 'react'
import { bool, func } from 'prop-types'
import styled from 'styled-components'
import { rem } from 'polished'
import { Flex, Box } from 'rebass'
import { Text, Image } from 'rebass'

import { Container, media } from '../../../utils/style'
import buildImageUrl from '../../../../lib/media-url-maker'

import LinkRounded from '../../LinkRounded'

const Content = styled(Flex)`
  position: relative;
  background: white;
  line-height: 1.8;
  overflow-y: scroll;
  height: 92vh;
  font-weight: ${(props) => props.theme.weights[0]};
  ${media.LargeScreenUp`
    height: auto;
  `};
  img {
    ${media.LargeScreenUp`
      float: right;
    `};
  }
  a {
    color: inherit;
  }
`

const Annotation = styled(({ full, left, bottom, theme, width, ...rest }) => (
  <Box {...rest} />
))`
  font-size: ${(props) => rem(props.theme.fontSizes[1])};
  line-height: 1.5;
  width: 100%;
  ${media.LargeScreenUp`
  text-align: center;
  float: left;
  position: ${(props) => (props.full ? 'absolute' : 'relative')};
  left: ${(props) => props.left};
  bottom: ${(props) => props.bottom};
  width: ${(props) => rem(props.width)};
  `};
  strong {
    font-size: ${(props) => rem(props.theme.fontSizes[2])};
    color: ${(props) => props.theme.colors.blue};
    display: block;
    margin-bottom: 5px;
  }
`

const Diagram = styled(Box)`
  position: relative;
`

const Features = styled(Box)`
  text-align: left;
  strong {
    display: block;
    ${media.LargeScreenUp`
    margin-left: -15px;
    `};
    margin-bottom: 10px;
    color: ${(props) => props.theme.colors.blue};
  }
`

const OrderedList = styled.ol`
  list-style: none;
  text-align: left;
  margin: 0 5%;
  ${media.LargeScreenUp`
  margin: 0 25% 0 0;
  `};
  counter-reset: li;
  li {
    counter-increment: li;
  }
  li::before {
    content: counter(li);
    color: ${(props) => props.theme.colors.blue};
    display: inline-block;
    width: 1em;
    margin-left: -1.5em;
    margin-right: 0.8em;
    text-align: right;
    direction: rtl;
    font-weight: ${(props) => props.theme.weights[1]};
  }
`

const ActionContainer = styled.div`
  position: relative;
  margin-top: 32px;
  ${media.LargeScreenUp`
    left: -15px;
  `};

  a,
  button {
    color: ${(props) => props.theme.colors.white};
  }
`

const ActionContainerAlt = styled.div`
  text-align: center;
  width: 100%;
  margin-top: 16px;

  a,
  button {
    background: transparent;
    border: 0;
  }
`

const VideoContainer = styled.div`
  width: 100%;
  height: 0;
  padding-bottom: 56.25%;
  overflow: hidden;
  position: relative;
  background-color: ${(props) => props.theme.colors.grey_op10};

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`

const PCP = ({ isVideoVisible, onVideoToggle }) => (
  <Content flexWrap="wrap" px={'8.333%'} pt={45} pb={[65]} width={1}>
    {!isVideoVisible && (
      <Fragment>
        <Box width={1}>
          <Container maxWidth={['100%', null, '85%']} pb={45}>
            <Text center>
              A personal contact purchase (PCP) will guarantee the minimum
              future value of your vehicle, based on the annual mileage you
              choose. It’s a simple way to budget for a regular change of car,
              regardless of how car values fluctuate. Come in store or contact
              Citygate to learn more.{' '}
              <a href="/legal" target="_blank" rel="noopener">
                Terms&nbsp;&&nbsp;Conditions
              </a>{' '}
              apply.
            </Text>
          </Container>
        </Box>
        <Features width={[1, null, 4 / 12]}>
          <strong>What affects my monthly payment?</strong>
          <OrderedList>
            <li>The length of repayment period you choose</li>
            <li>Your annual mileage</li>
            <li>Optional final payment</li>
          </OrderedList>
          <ActionContainer>
            <LinkRounded onClick={onVideoToggle}>Watch video</LinkRounded>
          </ActionContainer>
        </Features>
        <Diagram width={[1, null, 8 / 12]} pb={[85, null, 0]}>
          <Image
            src={buildImageUrl({
              publicId: 'diagram-what-is-pcp_2x'
            })}
            width={'100%'}
            mt={[35, null, 0]}
            mb={[40, null, 15]}
            mx={['auto', 0]}
          />
          <Annotation bottom={0} left={'0'} width={150} pb={[20, null, 0]}>
            <strong>Deposit</strong>
            From as little as one monthly payment, or more if you like
          </Annotation>
          <Annotation
            bottom={0}
            left={'37%'}
            full
            width={150}
            pb={[20, null, 0]}
          >
            <strong>Monthly payments</strong>
            Spread across 18 - 48 months on a schedule to suit you
          </Annotation>
          <Annotation bottom={0} left={'72%'} full width={180}>
            <strong>Optional final payment</strong>
            Your choice to buy, part-exchange for a new one, or hand it back &
            walk away
          </Annotation>
        </Diagram>
      </Fragment>
    )}

    {isVideoVisible && (
      <Box width={1}>
        <Container maxWidth={['100%', null, '85%']}>
          <VideoContainer>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/6lvY9pxoieM"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </VideoContainer>
        </Container>
        <ActionContainerAlt>
          <button onClick={onVideoToggle}>Back to description</button>
        </ActionContainerAlt>
      </Box>
    )}
  </Content>
)

PCP.propTypes = {
  isVideoVisible: bool,
  onVideoToggle: func
}

export default PCP
